<template>
  <div
    :class="classes"
    :style="styles"
  >
    <div
      class="input-container"
    >
      <BaseInput
        v-model="internalInput"
        :type="type"
        :clearable="false"
        :label="label"
        :input-suffix="inputSuffix"
        :max-decimals="maxDecimals"
      />
      <BaseSelect
        ref="select"
        v-model="internalValue"
        :root-class="rootClass"
        filled="#f2f2f2"
        :items="items"
        :shadow-items="shadowItems"
        :clearable="false"
        @listActive="listActive = $event"
      />
    </div>
    <div
      v-if="hasError || hint || counter"
      class="input-hint"
    >
      <div
        v-if="hasError"
        class="input-hint--error"
        v-html="errorMessage"
      />
      <div
        v-if="hint && !hasError"
        class="input-hint--hint"
      >
        <span>{{ hint }}</span>
      </div>
      <div
        v-if="counter"
        class="input-hint--counter"
      >
        <span>{{ value.length }} {{ maxLength > 0 ? ` | ${maxLength}` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInputMixin from '../../../shared/mixins/BaseInputMixin';

const VALID_TYPES = ['text', 'number', 'email', 'phone', 'password', 'hidden', 'tel', 'percentage'];

export default {
  name: 'BaseInputSelect',
  mixins: [BaseInputMixin],
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (value) => VALID_TYPES.includes(value),
    },
    items: {
      type: Array,
      required: true,
    },
    input: {
      type: [String, Number, Boolean],
      default: '',
    },
    value: {
      type: [String, Number, Array, Object],
      required: false,
      default: () => null,
    },
    shadowItems: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    inputSuffix: {
      type: String,
      default: '',
    },
    alwaysMaxWidth: {
      type: Boolean,
      default: false,
    },
    maxWidthFromArray: {
      type: Array,
      default: () => [],
    },
    maxDecimals: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:input', 'update:value'],
  data() {
    return {
      listActive: false,
      internalValue: this.value,
      internalInput: this.input,
      minWidth: 'unset',
    };
  },
  computed: {
    componentId() {
      return `${this.classDefault}-${this.IOH.randomId()}`;
    },
    rootClass() {
      return `.${this.componentId} > .input-container`;
    },
  },
  watch: {
    value(value) {
      this.internalValue = value;
    },
    input(value) {
      this.internalInput = value;
    },
    internalValue(value) {
      this.$emit('update:value', value);
    },
    internalInput(value) {
      this.$emit('update:input', value);
    },
    alwaysMaxWidth(value) {
      if (value) {
        this.calculateMinWidth();
      }
    },
    maxWidthFromArray(value) {
      if (value) {
        this.calculateMinWidth(value);
      }
    },
  },
  mounted() {
    if (this.alwaysMaxWidth) {
      calculateMinWidth();
    } else if (this.maxWidthFromArray) {
      this.calculateMinWidth(this.maxWidthFromArray);
    }
  },
  methods: {
    ClassesHook() {
      return {
        [this.componentId]: true,
        [`${this.classDefault}--open`]: this.listActive,
      };
    },
    calculateMinWidth(list = null) {
      let currentList = list;

      if (list === null) {
        currentList = this.shadowItems.length ? this.shadowItems : this.items;
      }

      if (currentList.some((item) => item.label === null)) {
        return;
      }

      const selectStyles = getComputedStyle(this.$refs.select.$el.querySelector('.input-selected-item'));
      let width = 0;

      const dummy = document.createElement('span');
      dummy.style = selectStyles;
      dummy.style.height = '0px';

      currentList.forEach((item) => {
        dummy.innerText = item.label;
        document.body.appendChild(dummy);
        const itemWidth = dummy.getBoundingClientRect().width;
        if (itemWidth > width) {
          width = itemWidth;
        }
        dummy.remove();
      });

      if (width > 0) {
        this.minWidth = `${width}px`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.input-container {
  display: grid;
  grid-template-columns: 1fr auto;
}

.base-select :deep(.input-container) {
  border-radius: 0;
}

.base-select :deep(.input-selected-item) {
  min-width: v-bind(minWidth);
}
</style>
